import React, { useMemo, useState } from 'react'
import { Table, Button, Alert } from 'react-bootstrap'
import { formatCurrency } from '../../../constants/constant-func'

const errorText = (value) => {
    if (value == null || value === '') return null
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    if (Array.isArray(value)) {
        const parts = value.map(errorText).filter(Boolean)
        return parts.length ? parts.join(' ') : null
    }
    if (typeof value === 'object') {
        if (value.message) return errorText(value.message)
        if (value.error) return errorText(value.error)
        try {
            return JSON.stringify(value)
        } catch (e) {
            return null
        }
    }
    return null
}

const isQuoteRow = (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false
    return item.banca != null || item.capitaleLordo != null || item.importoRata != null || item.nettoRicavo != null
}

const SortHeader = ({ field, label, sortBy, sortDir, onSort, className }) => {
    const isActive = sortBy === field
    return (
        <th
            scope="col"
            className={className}
            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
            onClick={() => onSort(field)}
        >
            {label}
            <span className="ml-1 text-muted" style={{ fontSize: '0.75em' }}>
                {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
            </span>
        </th>
    )
}

const num = (v) => {
    const n = parseFloat(v)
    return isNaN(n) ? 0 : n
}

const PreventiviResultsTable = ({
    results,
    percentualeProvvigione = 0,
    totalResiduoDaEstinguere = 0,
    showNettoCliente = false,
    showActions = false,
    actionLabel = 'Crea Opportunità',
    onAction = null,
}) => {
    const [sortBy, setSortBy] = useState(null)
    const [sortDir, setSortDir] = useState('asc')

    const { flattened, bankErrors } = useMemo(() => {
        if (!results || typeof results !== 'object') {
            return { flattened: [], bankErrors: [] }
        }

        const quotes = []
        const errors = []

        Object.entries(results).forEach(([bankKey, value]) => {
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (isQuoteRow(item)) {
                        quotes.push({ ...item, _group: bankKey })
                        return
                    }
                    const message = errorText(item && typeof item === 'object' ? item.error : item)
                    if (message) errors.push({ bank: bankKey, message })
                })
                return
            }

            const message = bankKey === 'error'
                ? errorText(value)
                : errorText(value && typeof value === 'object' ? value.error : value)
            if (message) {
                errors.push({ bank: bankKey === 'error' ? null : bankKey, message })
            }
        })

        return { flattened: quotes, bankErrors: errors }
    }, [results])

    const provvigione = parseFloat(percentualeProvvigione) || 0

    const rows = useMemo(() => {
        const data = flattened.map((item) => ({
            ...item,
            _provvigioneValue: num(item.capitaleLordo) * provvigione / 100,
            _nettoCliente: num(item.nettoRicavo) - (parseFloat(totalResiduoDaEstinguere) || 0),
        }))

        if (!sortBy) return data

        const getValue = (row) => {
            switch (sortBy) {
                case 'banca':
                    return (row.banca || '').toString().toLowerCase()
                case 'assicurazione':
                    return (row.assicurazione || '').toString().toLowerCase()
                case 'capitaleLordo':
                    return num(row.capitaleLordo)
                case 'importoRata':
                    return num(row.importoRata)
                case 'nettoRicavo':
                    return num(row.nettoRicavo)
                case 'nettoCliente':
                    return row._nettoCliente
                case 'provvigione':
                    return row._provvigioneValue
                case 'TAN':
                    return num(row.TAN)
                case 'TAEG':
                    return num(row.TAEG)
                case 'TEG':
                    return num(row.TEG)
                case 'totaleAssicurazione':
                    return num(row.totaleAssicurazione)
                default:
                    return 0
            }
        }

        const sorted = [...data].sort((a, b) => {
            const av = getValue(a)
            const bv = getValue(b)
            if (av < bv) return sortDir === 'asc' ? -1 : 1
            if (av > bv) return sortDir === 'asc' ? 1 : -1
            return 0
        })

        return sorted
    }, [flattened, sortBy, sortDir, provvigione, totalResiduoDaEstinguere])

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(field)
            setSortDir('asc')
        }
    }

    const errorAlerts = bankErrors.length > 0 && (
        <div className={flattened.length > 0 ? 'mb-3' : undefined}>
            {bankErrors.map((entry, index) => (
                <Alert key={`${entry.bank || 'error'}-${index}`} variant="danger" className={index < bankErrors.length - 1 ? 'mb-2' : 'mb-0'}>
                    {entry.bank ? <><strong>{entry.bank}:</strong> {entry.message}</> : entry.message}
                </Alert>
            ))}
        </div>
    )

    if (!results || (flattened.length === 0 && bankErrors.length === 0)) {
        return <div className="text-center text-muted py-3">Nessun risultato disponibile.</div>
    }

    if (flattened.length === 0) {
        return errorAlerts
    }

    const baseColumns = [
        { field: 'banca', label: 'Banca' },
        { field: 'assicurazione', label: 'Assicurazione' },
        { field: 'capitaleLordo', label: 'Capitale Lordo' },
        { field: 'importoRata', label: 'Importo Rata' },
        { field: 'nettoRicavo', label: 'Netto Ricavo' },
    ]

    if (showNettoCliente) {
        baseColumns.push({ field: 'nettoCliente', label: 'Netto Cliente' })
    }

    baseColumns.push(
        { field: 'provvigione', label: 'Provvigione' },
        { field: 'TAN', label: 'TAN' },
        { field: 'TAEG', label: 'TAEG' },
        { field: 'TEG', label: 'TEG' },
        { field: 'totaleAssicurazione', label: 'Totale Assicurazione' },
    )

    return (
        <>
        {errorAlerts}
        <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
                <thead>
                    <tr>
                        {baseColumns.map((col) => (
                            <SortHeader
                                key={col.field}
                                field={col.field}
                                label={col.label}
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSort={handleSort}
                            />
                        ))}
                        <th>Errore</th>
                        {showActions && <th>Azioni</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((item, index) => (
                        <tr
                            key={index}
                            style={{
                                backgroundColor: item.PreventivoInErrore ? '#ffebee' : 'transparent',
                            }}
                        >
                            <td>{item.banca}</td>
                            <td>{item.assicurazione}</td>
                            <td>{formatCurrency(item.capitaleLordo, 0)}</td>
                            <td>{formatCurrency(item.importoRata, 1)}</td>
                            <td>{formatCurrency(item.nettoRicavo, 0)}</td>
                            {showNettoCliente && (
                                <td>{formatCurrency(item._nettoCliente, 0)}</td>
                            )}
                            <td>
                                {formatCurrency(item._provvigioneValue, 0)} ({provvigione.toFixed(1)}%)
                            </td>
                            <td>{Number(item.TAN).toFixed(2)}%</td>
                            <td>{Number(item.TAEG).toFixed(2)}%</td>
                            <td>{Number(item.TEG).toFixed(2)}%</td>
                            <td>{formatCurrency(item.totaleAssicurazione, 0)}</td>
                            <td>{item.DescrizioneErrorePreventivo ? item.DescrizioneErrorePreventivo : '-'}</td>
                            {showActions && (
                                <td>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => onAction && onAction(item)}
                                    >
                                        {actionLabel}
                                    </Button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
        </>
    )
}

export default PreventiviResultsTable
